import type { ReactNode, TextareaHTMLAttributes } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';

import {
  IconWrapStyled,
  LabelStyled,
  RequiredSignStyled,
  TextAreaStyled,
  TextAreaWrapStyled,
} from './TextArea.styled';

type TextAreaPropsT = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  name?: string;
  label?: string | ReactNode;
  icon?: ReactNode;
  bg?: 'grey';
  isRequired?: boolean;
  resizable?: boolean;
  rows: number;
};

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaPropsT>(
  ({ name, label, icon, bg, isRequired, resizable, rows = 1, ...restProps }, ref) => {
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

    const [textareaHeight, setTextareaHeight] = useState('auto');

    const updateTextareaHeight = (): void => {
      const scrollHeight = textAreaRef.current?.scrollHeight || 0;
      setTextareaHeight(scrollHeight + 'px');
    };

    useEffect(() => {
      updateTextareaHeight();
    }, []);

    const handleInput = (_e: React.FormEvent<HTMLTextAreaElement>): void => {
      updateTextareaHeight();
    };

    return (
      <TextAreaWrapStyled>
        {label && (
          <LabelStyled htmlFor={name}>
            {label}
            {isRequired && <RequiredSignStyled>&nbsp;*</RequiredSignStyled>}
          </LabelStyled>
        )}
        {icon && <IconWrapStyled>{icon}</IconWrapStyled>}
        <TextAreaStyled
          id={name}
          name={name}
          ref={(el): void => {
            textAreaRef.current = el;
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
          }}
          bg={bg}
          rows={rows}
          style={{ height: resizable ? textareaHeight : 'auto', overflowY: 'hidden' }}
          onInput={handleInput}
          {...restProps}
        />
      </TextAreaWrapStyled>
    );
  }
);

export default TextArea;
